/**
 * @prettier
 **/
// @ts-check

import {
	ringTreeService,
	pingTreeTargetService,
	columnsFetchService
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { cloneDeep, map, filter } from 'lodash';
import {
	SET_RING_TREES,
	SET_RING_TREE_TARGETS,
	SET_RING_TREE,
	SET_WIDGET_TITLE as SET_WIDGET_TITLE_MUTATION,
	SET_USER_VISIBLE_COLUMNS,
	SET_RING_TREE_REFERENCES,
	SET_CHECK_REFERENCES_TEXT
} from './mutationTypes';
import {
	GET_RING_TREES,
	GET_RING_TREE,
	GET_RING_TREE_TARGETS,
	CREATE_RING_TREE,
	UPDATE_RING_TREE,
	TRIGGER_RING_TREE_CREATE_PENDING_STATUS,
	TRIGGER_RING_TREE_UPDATE_PENDING_STATUS,
	UPDATE_RING_TREE_PROPERTY,
	DELETE_RING_TREE,
	SET_WIDGET_TITLE,
	GET_USER_VISIBLE_COLUMNS,
	GET_RING_TREE_REFERENCES
} from './actionTypes';
import {
	SET_ACTION_STATUS_PENDING,
	SET_ACTION_STATUS_SUCCESS,
	SET_ACTION_STATUS_ERROR
} from '@/store/helpers';

const _updateStat = (ringTree, stats) => {
	ringTree.usageStats = stats.usageStats;
	ringTree.tStatus = stats.status;
};

const updateTargets = (
	currentAccountId,
	ringTreeId,
	addTargets,
	removeTargets
) => {
	if (
		(addTargets && addTargets.length > 0) ||
		(removeTargets && removeTargets.length > 0)
	) {
		return ringTreeService.updateTargets(
			currentAccountId,
			ringTreeId,
			addTargets.map((target) => target.id),
			removeTargets.map((target) => target.id)
		);
	} else {
		return Promise.resolve();
	}
};

const _updateTargetsAndRoutes = (
	currentAccountId,
	ringTree,
	newTargets,
	removedTargets
) => {
	const promises = [];
	promises.push(
		updateTargets(currentAccountId, ringTree.id, newTargets, removedTargets)
	);
	return Promise.all(promises);
};

// @ts-ignore
/** @type {import('vuex').ActionTree} */
const actions = {
	async [SET_WIDGET_TITLE]({ commit }, { title }) {
		commit(SET_WIDGET_TITLE_MUTATION, title);
	},

	/**
	 * Get a Ring Tree
	 */
	async [GET_RING_TREE]({ rootState, commit, state }, { ringTreeId }) {
		commit(SET_ACTION_STATUS_PENDING, { actionName: GET_RING_TREE });
		commit(SET_RING_TREES, []);
		try {
			const response = await ringTreeService.getPingTree(
				rootState.currentAccountId,
				ringTreeId
			);
			const ringTrees = [response];
			const columns = columnsFetchService.getColumnList(
				{},
				ringTreeService.defaultColumns,
				'publishers',
				state.columnSettings
			);
			commit(SET_RING_TREES, ringTrees);
			commit(SET_USER_VISIBLE_COLUMNS, columns);
			commit(SET_WIDGET_TITLE_MUTATION, `Ring Tree: ${ringTrees[0].name}`);
			commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_RING_TREE });
		} catch (error) {
			commit(SET_ACTION_STATUS_ERROR, { actionName: GET_RING_TREE, error });
		}
	},

	/**
	 * Get all Ring Trees
	 */
	async [GET_RING_TREES]({ rootState, commit }) {
		commit(SET_ACTION_STATUS_PENDING, { actionName: GET_RING_TREES });
		try {
			const response = await ringTreeService.getPingTrees(
				rootState.currentAccountId,
				true
			);
			const ringTrees = response.data.pingTrees;
			commit(SET_RING_TREES, ringTrees);
			commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_RING_TREES });
		} catch (e) {
			commit(SET_ACTION_STATUS_ERROR, { actionName: GET_RING_TREES, error: e });
		}
	},

	/**
	 * Get Ring Tree Targets
	 */
	async [GET_RING_TREE_TARGETS]({ rootState, commit }) {
		commit(SET_ACTION_STATUS_PENDING, { actionName: GET_RING_TREE_TARGETS });
		try {
			const ringTreeTargets = await pingTreeTargetService.getTargets(
				rootState.currentAccountId
			);
			commit(SET_RING_TREE_TARGETS, ringTreeTargets);
			commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_RING_TREE_TARGETS });
		} catch (e) {
			commit(SET_ACTION_STATUS_ERROR, {
				actionName: GET_RING_TREE_TARGETS,
				error: e
			});
		}
	},

	/**
	 * Get ring tree references
	 */
	async [GET_RING_TREE_REFERENCES]({ rootState, commit }, { ringTreeId }) {
		commit(
			SET_CHECK_REFERENCES_TEXT,
			'DELETE_PING_TREE_MODAL_TARGET_CHECK_ASSOCIATION_TEXT'
		);
		commit(SET_ACTION_STATUS_PENDING, { actionName: GET_RING_TREE_REFERENCES });
		commit(SET_RING_TREE_REFERENCES, null);
		try {
			const response = await ringTreeService.getPingTreeReferences(
				rootState.currentAccountId,
				ringTreeId
			);
			commit(SET_RING_TREE_REFERENCES, response.data);
			commit(SET_ACTION_STATUS_SUCCESS, {
				actionName: GET_RING_TREE_REFERENCES
			});
		} catch (error) {
			commit(
				SET_CHECK_REFERENCES_TEXT,
				'DELETE_PING_TREE_MODAL_TARGET_CHECK_ASSOCIATION_ERROR_TEXT'
			);
			commit(SET_ACTION_STATUS_ERROR, {
				actionName: GET_RING_TREE_REFERENCES,
				error
			});
		}
	},

	/**
	 * Create a ring tree
	 */
	async [CREATE_RING_TREE](
		{ rootState, commit },
		{ ringTree, newTargets, removedTargets }
	) {
		commit(SET_ACTION_STATUS_PENDING, { actionName: CREATE_RING_TREE });

		try {
			const response = await ringTreeService.createPingTree(
				rootState.currentAccountId,
				ringTree
			);
			await _updateTargetsAndRoutes(
				rootState.currentAccountId,
				response,
				newTargets,
				removedTargets
			);
			commit(SET_ACTION_STATUS_SUCCESS, {
				actionName: CREATE_RING_TREE,
				data: response
			});
			return response;
		} catch (error) {
			commit(SET_ACTION_STATUS_ERROR, { actionName: CREATE_RING_TREE, error });
		}
	},

	/**
	 * Update a ring tree property
	 */
	async [UPDATE_RING_TREE_PROPERTY](
		{ rootState, commit, state },
		{ ringTree, status }
	) {
		commit(SET_ACTION_STATUS_PENDING, {
			actionName: UPDATE_RING_TREE_PROPERTY
		});
		try {
			const response = await ringTreeService.updatePingTreeProperty(
				rootState.currentAccountId,
				ringTree.id,
				{ enabled: status }
			);
			const updatedRingTree = response.data.pingTree;
			_updateStat(updatedRingTree, response.data.stats);
			const index = state.ringTrees.findIndex(
				(rt) => rt.id === updatedRingTree.id
			);
			commit(SET_RING_TREE, { index: index, ringTree: updatedRingTree });
			commit(SET_ACTION_STATUS_SUCCESS, {
				actionName: UPDATE_RING_TREE_PROPERTY
			});
		} catch (error) {
			commit(SET_ACTION_STATUS_ERROR, {
				actionName: UPDATE_RING_TREE_PROPERTY,
				error
			});
		}
	},

	/**
	 * Update a ring tree
	 */
	async [UPDATE_RING_TREE](
		{ rootState, commit },
		{ ringTree, newTargets, removedTargets }
	) {
		commit(SET_ACTION_STATUS_PENDING, { actionName: UPDATE_RING_TREE });
		try {
			await ringTreeService.updatePingTree(
				rootState.currentAccountId,
				ringTree
			);
			await _updateTargetsAndRoutes(
				rootState.currentAccountId,
				ringTree,
				newTargets,
				removedTargets
			);

			const response = await ringTreeService.getPingTrees(
				rootState.currentAccountId,
				true
			);
			const ringTrees = response.data.pingTrees;
			const index = ringTrees.findIndex((rt) => rt.id === ringTree.id);
			const updatedRingTree = ringTrees[index];

			commit(SET_ACTION_STATUS_SUCCESS, { actionName: UPDATE_RING_TREE });
			return updatedRingTree;
		} catch (error) {
			commit(SET_ACTION_STATUS_ERROR, { actionName: UPDATE_RING_TREE, error });
		}
	},

	/**
	 * Delete a ring tree
	 */
	async [DELETE_RING_TREE](
		{ rootState, commit, state },
		{ ringTree, forceUnlink }
	) {
		commit(SET_ACTION_STATUS_PENDING, { actionName: DELETE_RING_TREE });
		try {
			await ringTreeService.deletePingTree(
				rootState.currentAccountId,
				ringTree.id,
				forceUnlink
			);
			const arrayToModify = cloneDeep(state.ringTrees);
			arrayToModify.splice(
				state.ringTrees.findIndex((rt) => rt.id === ringTree.id),
				1
			);
			commit(SET_RING_TREES, arrayToModify);
			commit(SET_ACTION_STATUS_SUCCESS, { actionName: DELETE_RING_TREE });
		} catch (error) {
			commit(SET_ACTION_STATUS_ERROR, { actionName: DELETE_RING_TREE, error });
		}
	},

	/*
	 * Get user visible columns
	 */
	async [GET_USER_VISIBLE_COLUMNS]({ commit }) {
		commit(SET_ACTION_STATUS_PENDING, { actionName: GET_USER_VISIBLE_COLUMNS });
		commit(SET_USER_VISIBLE_COLUMNS, []);
		try {
			let userVisibleColumns = map(
				filter(ringTreeService.columnSettings, (col) => !col.hide),
				(col) => {
					return col.name;
				}
			);
			commit(SET_USER_VISIBLE_COLUMNS, userVisibleColumns);
			commit(SET_ACTION_STATUS_SUCCESS, {
				actionName: GET_USER_VISIBLE_COLUMNS
			});
		} catch (e) {
			commit(SET_ACTION_STATUS_ERROR, {
				actionName: GET_USER_VISIBLE_COLUMNS,
				error: e
			});
		}
	}
};

export default actions;
